export type MediaKind = "image" | "audio" | "video" | "pdf";

export interface MediaType {
  kind: MediaKind;
  mime: string;
}

const byExtension: Record<string, MediaType> = {
  png: { kind: "image", mime: "image/png" },
  apng: { kind: "image", mime: "image/apng" },
  jpg: { kind: "image", mime: "image/jpeg" },
  jpeg: { kind: "image", mime: "image/jpeg" },
  gif: { kind: "image", mime: "image/gif" },
  webp: { kind: "image", mime: "image/webp" },
  avif: { kind: "image", mime: "image/avif" },
  bmp: { kind: "image", mime: "image/bmp" },
  ico: { kind: "image", mime: "image/x-icon" },
  cur: { kind: "image", mime: "image/x-icon" },
  mp3: { kind: "audio", mime: "audio/mpeg" },
  wav: { kind: "audio", mime: "audio/wav" },
  ogg: { kind: "audio", mime: "audio/ogg" },
  oga: { kind: "audio", mime: "audio/ogg" },
  m4a: { kind: "audio", mime: "audio/mp4" },
  aac: { kind: "audio", mime: "audio/aac" },
  flac: { kind: "audio", mime: "audio/flac" },
  opus: { kind: "audio", mime: "audio/ogg" },
  mp4: { kind: "video", mime: "video/mp4" },
  m4v: { kind: "video", mime: "video/mp4" },
  webm: { kind: "video", mime: "video/webm" },
  ogv: { kind: "video", mime: "video/ogg" },
  mov: { kind: "video", mime: "video/quicktime" },
  pdf: { kind: "pdf", mime: "application/pdf" },
};

function startsWith(bytes: Uint8Array, signature: number[], offset = 0): boolean {
  if (bytes.byteLength < offset + signature.length) return false;
  return signature.every((value, index) => bytes[offset + index] === value);
}

function ascii(text: string): number[] {
  return [...text].map((character) => character.charCodeAt(0));
}

/** Content sniffing for the formats browsers render, so a misnamed or extensionless file still previews. */
export function sniffMediaType(bytes: Uint8Array): MediaType | undefined {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return { kind: "image", mime: "image/png" };
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return { kind: "image", mime: "image/jpeg" };
  if (startsWith(bytes, ascii("GIF87a")) || startsWith(bytes, ascii("GIF89a"))) return { kind: "image", mime: "image/gif" };
  if (startsWith(bytes, ascii("RIFF")) && startsWith(bytes, ascii("WEBP"), 8)) return { kind: "image", mime: "image/webp" };
  if (startsWith(bytes, ascii("RIFF")) && startsWith(bytes, ascii("WAVE"), 8)) return { kind: "audio", mime: "audio/wav" };
  if (startsWith(bytes, ascii("BM"))) return { kind: "image", mime: "image/bmp" };
  if (startsWith(bytes, [0x00, 0x00, 0x01, 0x00])) return { kind: "image", mime: "image/x-icon" };
  if (startsWith(bytes, ascii("%PDF-"))) return { kind: "pdf", mime: "application/pdf" };
  if (startsWith(bytes, ascii("fLaC"))) return { kind: "audio", mime: "audio/flac" };
  if (startsWith(bytes, ascii("OggS"))) return { kind: "audio", mime: "audio/ogg" };
  if (startsWith(bytes, ascii("ID3"))) return { kind: "audio", mime: "audio/mpeg" };
  if (startsWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return { kind: "video", mime: "video/webm" };
  if (startsWith(bytes, ascii("ftyp"), 4)) {
    const brand = String.fromCharCode(...bytes.slice(8, 12));
    if (brand === "avif" || brand === "avis") return { kind: "image", mime: "image/avif" };
    if (brand.startsWith("M4A")) return { kind: "audio", mime: "audio/mp4" };
    if (brand === "qt  ") return { kind: "video", mime: "video/quicktime" };
    return { kind: "video", mime: "video/mp4" };
  }
  return undefined;
}

export function extensionMediaType(path: string): MediaType | undefined {
  const name = path.split("/").at(-1) ?? "";
  if (!name.includes(".")) return undefined;
  return byExtension[name.split(".").at(-1)!.toLowerCase()];
}

/** Bytes win over the extension: they describe what the browser will actually be asked to decode. */
export function mediaTypeFor(path: string, sides: Array<Uint8Array | undefined>): MediaType | undefined {
  for (const bytes of sides) {
    if (!bytes || bytes.byteLength === 0) continue;
    const sniffed = sniffMediaType(bytes);
    if (sniffed) return sniffed;
  }
  return extensionMediaType(path);
}

export function decodeSnapshot(value: string, encoding: "utf8" | "base64" | undefined): Uint8Array {
  if (encoding !== "base64") return new TextEncoder().encode(value);
  const decoded = atob(value);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export function formatByteDelta(before: number, after: number): string {
  const delta = after - before;
  if (delta === 0) return "same size";
  const sign = delta > 0 ? "+" : "−";
  const percent = before > 0 ? ` (${sign}${Math.abs(Math.round((delta / before) * 100))}%)` : "";
  return `${sign}${formatBytes(Math.abs(delta))}${percent}`;
}
